import React, { useCallback, useEffect, useState } from "react";
import { Check, Video } from "lucide-react";
import logger from "../../utils/logger";
import { subscribeToMediaDeviceChange } from "../../utils/mediaDeviceChange";
import { isBackCameraLabel, isMobileOrTablet } from "../../utils/media";

export interface VideoDevice {
  deviceId: string;
  label: string;
}

interface CameraSelectorProps {
  onSelect?: (device: VideoDevice) => void;
  videoId?: string;
}

const CameraSelector: React.FC<CameraSelectorProps> = ({
  videoId,
  onSelect,
}) => {
  const [devices, setDevices] = useState<VideoDevice[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const loadCameras = useCallback(
    async (requestPermission = false) => {
      try {
        if (requestPermission) {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: isMobileOrTablet() ? { facingMode: "user" } : true,
          });
          stream.getTracks().forEach((track) => track.stop());
        }

        const all = await navigator.mediaDevices.enumerateDevices();
        const cameras = all
          .filter((device) => device.kind === "videoinput")
          // Phones/tablets are locked to the front camera — hide rear ones
          .filter(
            (device) =>
              !isMobileOrTablet() || !isBackCameraLabel(device.label),
          )
          .map((device) => ({
            deviceId: device.deviceId,
            label: device.label || "Camera",
          }));

        setDevices(cameras);
        setSelectedId((currentId) => {
          const next =
            cameras.find((camera) => camera.deviceId === videoId) ||
            cameras.find((camera) => camera.deviceId === currentId) ||
            cameras[0];

          if (currentId && next && currentId !== next.deviceId) {
            onSelect?.(next);
          }

          return next?.deviceId || null;
        });
      } catch (err) {
        logger.error("❌ Camera access error:", err);
      }
    },
    [onSelect, videoId],
  );

  /* ------------------ Load Cameras ------------------ */
  useEffect(() => {
    loadCameras(true);

    return subscribeToMediaDeviceChange(() => loadCameras(false));
  }, [loadCameras]);

  /* ------------------ User Selection ------------------ */
  const handleSelect = (camera: VideoDevice) => {
    if (camera.deviceId === selectedId) return; // prevent re-select

    setSelectedId(camera.deviceId);
    onSelect?.(camera);
  };

  return (
    <div className="w-[420px] bg-gray-50 rounded-xl shadow-xl border py-2">
      {devices.map((cam, idx) => (
        <button
          key={cam.deviceId}
          type="button"
          onClick={() => handleSelect(cam)}
          className={`flex items-start gap-3 w-full px-4 py-3 text-left
            hover:bg-gray-100 ${
              selectedId === cam.deviceId ? "bg-gray-100" : ""
            }`}
        >
          <div className="w-5 pt-1">
            {selectedId === cam.deviceId && (
              <Check className="w-5 h-5 text-blue-600" />
            )}
          </div>

          <div>
            <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
              <Video className="w-4 h-4" />
              {cam.label}
            </div>

            {idx === 0 && (
              <div className="text-xs text-gray-500">System default</div>
            )}
          </div>
        </button>
      ))}
    </div>
  );
};

export default CameraSelector;
